/**
 * Utility functions for dish form handling
 */

/**
 * Constants for dish status
 */
export const DISH_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING'
};

/**
 * Constants for dish types
 */
export const DISH_TYPES = {
  DESSERT: 'DESSERT'
};

/**
 * Converts API spice level string (e.g. "not-spicy") to camelCase for translations.spiceLevels.* keys.
 * @param {string} apiKey
 * @returns {string}
 */
export const spiceApiKeyToTranslationKey = (apiKey) => {
  if (!apiKey || typeof apiKey !== "string") {
    return "";
  }
  return apiKey
    .split("-")
    .map((word, idx) => (idx === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join("");
};

/**
 * Formats an addition option for select components
 * @param {Object} addition - The addition object
 * @param {string} language - The current language
 * @returns {Object} Formatted option object
 */
export const formatAdditionOption = (addition, language) => {
  return {
    value: addition.id,
    label: addition.nameHy || `Addition ${addition.id}`,
    addition: addition
  };
};

/**
 * Formats tags for select components
 * @param {Array} tags - Array of tag objects
 * @param {string} labelKey - The key to use for the label
 * @returns {Array} Formatted tag options
 */
export const formatTags = (tags, labelKey) => tags.map(tag => {
  const hyTranslation = tag.translations?.find(t => t.languageCode === 'hy');
  return {
    label: hyTranslation?.value || tag[labelKey] || '',
    value: tag.id,
    translations: tag.translations
  };
});

/**
 * Prepares form data for submission by cleaning up additions data
 * @param {Object} formData - The form data to clean
 * @returns {Object} Cleaned form data
 */
export const cleanFormDataForSubmission = (formData) => {
  const cleaned = {
    ...formData,
    dishAdditionDtoList: formData.dishAdditionDtoList.map(addition => {
      // Create a new object without relevantDishes
      const {relevantDishes, ...cleanAddition} = addition;
      return cleanAddition;
    })
  };

  // If spice level is "not spicy" (typically ID 1), ensure adjustableSpiceLevelDtoList is empty
  // This handles both create and update scenarios
  if (cleaned.spiceLevel === 1 || cleaned.spiceLevel === "1") {
    cleaned.adjustableSpiceLevelDtoList = [];
  }

  return cleaned;
};

/**
 * Initializes form data from dish data or with default values
 * @param {Object} dishData - Existing dish data (if any)
 * @param {string} chefId - The chef ID
 * @returns {Object} Initialized form data
 */
export const initializeFormData = (dishData, chefId) => {
  return {
    id: dishData?.id || "",
    nameHy: dishData?.nameHy || "",
    nameEn: dishData?.nameEn || "",
    nameRu: dishData?.nameRu || "",
    price: dishData?.price || 0,
    type: dishData?.type || "",
    expirationDate: dishData?.expirationDate || "",
    spiceLevel: dishData?.type === DISH_TYPES.DESSERT ? null : dishData?.spiceLevel || "",
    adjustableSpiceLevelDtoList: dishData?.adjustableSpiceLevelDtoList || [],
    dietaryOptionDtoList: dishData?.dietaryOptionDtoList || [],
    dishAdditionDtoList: dishData?.dishAdditionDtoList?.map(a => ({
      ...a,
      overridedPrice: a.overridedPrice ?? null
    })) || [],
    ingridientsDto: dishData?.ingridientsDto || [
      {
        ingredient: "",
        translations: [
          {languageCode: "hy", value: ""}
        ],
      },
    ],
    orderBefore: dishData?.orderBefore ?? 1,
    minimumOrderCount: dishData?.minimumOrderCount ?? 1,
    portionHy: dishData?.portionHy || "",
    portionEn: dishData?.portionEn || "",
    portionRu: dishData?.portionRu || "",
    dishTagDto: dishData?.dishTagDto || null,
    dishCuisineTagDto: dishData?.dishCuisineTagDto || null,
    url: dishData?.url || "",
    status: dishData?.status || DISH_STATUS.ACTIVE,
    chefId: chefId || "",
    adultsOnly: dishData?.adultsOnly ?? 0,
  };
};

/**
 * Gets the configured group IDs from dish data
 * @param {Object} dishData - The dish data
 * @returns {Array} Array of configured group IDs
 */
export const getConfiguredGroupIds = (dishData) => {
  const initialGroupIds = new Set();
  dishData?.dishAdditionDtoList?.forEach(add => {
    if (add.group?.id) {
      initialGroupIds.add(add.group.id);
    }
  });
  return Array.from(initialGroupIds);
};

/**
 * Processes additions data to separate ungrouped and grouped additions
 * @param {Array} allDishAdditions - All dish additions
 * @returns {Object} Object containing ungrouped additions and grouped additions map
 */
export const processAdditionsData = (allDishAdditions) => {
  const ungrouped = [];
  const groupedMap = new Map();

  allDishAdditions.forEach(addition => {
    if (addition.group && addition.group.id) {
      const groupId = addition.group.id;
      if (!groupedMap.has(groupId)) {
        groupedMap.set(groupId, []);
      }
      groupedMap.get(groupId).push(addition);
    } else {
      ungrouped.push(addition);
    }
  });

  return {ungroupedAdditions: ungrouped, groupedAdditionsMap: groupedMap};
};
